import { routes } from './routes'

type LoopSiteEnvironment = {
  NEXT_PUBLIC_LOOP_URL?: string
  NEXT_PUBLIC_ENV?: string
  NODE_ENV?: string
}

/**
 * The Loop site: the host the printed HubHive Loop QR points at.
 *
 * `hubhive.com/loop` is the Loop download page, but on a phone that has the
 * main HubHive app the OS hands any `hubhive.com` link to that app before the
 * site is ever asked — Android App Links have no path scoping, and the app's
 * manifest claims the whole host — and the app has no screen for it. A QR
 * that has to work on every phone therefore points at a host NO app claims.
 * The website serves the same page there (see `proxy.ts`), and the desktop
 * CTA's QR encodes it too.
 *
 * Mirrors `profinder-site.ts`: env-driven, so dev and local builds get a
 * host of their own.
 */
export function getLoopSiteUrl(source: LoopSiteEnvironment = {
  NEXT_PUBLIC_LOOP_URL: process.env.NEXT_PUBLIC_LOOP_URL,
  NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
  NODE_ENV: process.env.NODE_ENV,
}): string {
  const configured = source.NEXT_PUBLIC_LOOP_URL?.trim()
  const fallback = source.NEXT_PUBLIC_ENV === 'local' || source.NODE_ENV !== 'production'
    ? 'http://loop.localhost:8080'
    : source.NEXT_PUBLIC_ENV === 'development'
      ? 'https://loop-dev.hubhive.com'
      : 'https://loop.hubhive.com'
  const url = new URL(configured || fallback)
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error('NEXT_PUBLIC_LOOP_URL must be an HTTP(S) site URL without credentials, query, or fragment')
  }
  return url.toString().replace(/\/+$/, '')
}

/** Whether a request `Host` header names the Loop site (port ignored, case-insensitive). */
export function isLoopSiteHost(rawHost: string | null | undefined, source?: LoopSiteEnvironment): boolean {
  const hostname = (rawHost ?? '').trim().toLowerCase().replace(/:\d+$/, '')
  return hostname !== '' && hostname === new URL(getLoopSiteUrl(source)).hostname
}

/** The one path the Loop site serves, on the main site: the canonical Loop download page. */
export const LOOP_SITE_PAGE_PATH = routes.loop
